import { SignOutButton } from "@clerk/clerk-react";
import {
  BookMarked,
  BookOpen,
  Building2,
  CalendarDays,
  ChevronsUpDown,
  CreditCard,
  FolderCog,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  LogOut,
  Mail,
  Settings,
  ShieldCheck,
  Sparkles,
  UserPen,
  Users,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clerkEnabled } from "@/services/clerk";
import { REDIRECT_PARAM, afterSignOut } from "@/lib/redirects";
import { sayListservIsForMembers } from "@/lib/listserv";
import { useApplyCta, useOffersApplication } from "@/features/members/hooks/useApplyCta";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** The AFELL listserv: a Google Group run by Soft Network, joined by asking.
 *
 *  Called **Discussion** in the nav rather than "Listserv": the group is where
 *  members talk to each other today, and an in-app board will take that name
 *  over when it ships without anybody having to relearn the word. */
export const LISTSERV_URL = "https://groups.google.com/g/AFELL";

/** Soft Network's own events page, where the AFELL Zoom sessions are listed
 *  and registered for. Not a page of this app: the directory is one programme
 *  of Soft Network's, and this is the way back out to the rest of it. */
export const EVENTS_URL = "https://www.softnetwork.art/afell-events";
import type { AdminQueues, MemberProfile } from "@/features/members/members.types";
import { AfellMark } from "@/components/AfellMark";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

// Two pages with a preview anybody may read, then the one thing that is
// members-only. Reading order, and the order somebody meets them in.
const memberNav = [
  // `preview: true` badges the item for a reader who is not seeing the
  // whole thing. Chelsea asked for it on the Directory; Resources previews
  // on exactly the same terms, and badging one and not the other would say
  // the library is complete when it is capped too.
  { title: "Directory", url: "/directory", icon: Users, preview: true },
  { title: "Resources", url: "/resources", icon: BookOpen, preview: true },
  // The Google Group, outside the app; an in-app board is out of scope for
  // now. Absolute URL = rendered as an external link below. `membersOnly`
  // because the group is the one thing here with no preview to offer — the
  // link is replaced by a note saying so rather than sending a stranger to a
  // Google page that will turn them away.
  { title: "Discussion", url: LISTSERV_URL, icon: Mail, membersOnly: true },
  // Last, and pointing away: the Zoom sessions live on Soft Network's site and
  // are found and registered for there. Asked for at the 1 September
  // walkthrough, so members "can easily locate and register for Zoom calls",
  // and by Todd, who wanted it in the sidebar as well as on the landing page.
  // Deliberately not `membersOnly`: Soft Network publishes that page openly,
  // and turning somebody away from it here would be a fiction.
  { title: "Events", url: EVENTS_URL, icon: CalendarDays },
];

// Six, down from eleven. Activity joined the dashboard and applications joined
// members, because each pair answered one question from two pages. The audit
// log and the sent-notification outbox are maintenance rather than
// administration: they are read in /basement/ by whoever runs the site, and a
// site admin never needed a tab for either.
const adminNav = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard, exact: true },
  { title: "Members", url: "/admin/members", icon: ShieldCheck, badge: "pendingApplications" },
  { title: "Organizations", url: "/admin/organizations", icon: Building2, badge: "pendingOrganizations" },
  { title: "Resources", url: "/admin/resources", icon: FolderCog },
  { title: "Billing", url: "/admin/billing", icon: CreditCard },
  { title: "Settings", url: "/admin/settings", icon: Settings },
];

const moderatorNav = [
  { title: "Resources", url: "/admin/resources", icon: FolderCog },
];

/**
 * The signed-in identity, and the account actions behind it.
 *
 * Profile, billing and signing out are all "about me" rather than "about
 * AFELL", so they hang off the identity instead of competing with the
 * directory in the nav above.
 *
 * Billing is there for everyone, on every tier. It stopped being a
 * paid-members-only page when it became the one place money happens: taking out
 * your own membership once an application is approved, paying for other
 * people's, and donating. A free member has two of those three available to
 * them, so hiding the page would hide the way in.
 */
function IdentityMenu({
  member,
  isMember,
}: {
  member: MemberProfile;
  isMember: boolean;
}) {
  const { pathname } = useLocation();
  const applyLabel = useApplyCta();
  const offersApplication = useOffersApplication(isMember, member.listed);
  // Collapsed to the icon rail there is only room for the avatar, so the name,
  // the address and the chevron step out rather than spill over the edge.
  const identity = (
    <div className="hover:bg-sidebar-accent flex w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1.5 text-left group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
      <Avatar className="size-8">
        <AvatarImage src={member.image_url ?? undefined} alt={member.name} />
        <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        <span className="truncate font-medium">{member.name}</span>
        <span className="text-muted-foreground truncate text-xs">
          {member.email}
        </span>
      </div>
      <ChevronsUpDown className="text-muted-foreground size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
    </div>
  );

  if (!clerkEnabled) return identity;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="w-full">
          {identity}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        {/* Three groups: who you are; what you hold, with the way to more of
            it first when there is one; then help and the door. */}
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <UserPen data-slot="icon" /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/organizations">
            <Building2 data-slot="icon" /> Organizations
          </Link>
        </DropdownMenuItem>
        {/* Adding to the library is an account action, beside your organizations:
            the resources page itself is for finding something, and a form for
            adding one on top of it is a button most people never want. The
            library is promised to members, so the form is theirs too. */}
        {isMember && (
          <DropdownMenuItem asChild>
            <Link to="/resources/mine">
              <BookMarked data-slot="icon" /> My resources
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {/* Above billing: for somebody who is not a member yet, or is one but
            not yet listed (a sponsored newcomer), the thing to do is apply,
            and the sidebar's blue button is easy to miss once a page is
            scrolled. A free member *is* a member, so theirs is the offer to
            pay rather than to apply. */}
        {offersApplication && member.role === "member" && (
          <DropdownMenuItem asChild>
            <Link to="/apply" className="text-brand-ink focus:text-brand-ink">
              <Sparkles data-slot="icon" /> {applyLabel}
            </Link>
          </DropdownMenuItem>
        )}
        {isMember && member.tier === "free" && member.role === "member" && (
          <DropdownMenuItem asChild>
            <Link to="/billing" className="text-brand-ink focus:text-brand-ink">
              <Sparkles data-slot="icon" /> Subscribe
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/billing">
            <CreditCard data-slot="icon" /> Billing
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account">
            <KeyRound data-slot="icon" /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/help">
            <LifeBuoy data-slot="icon" /> Help
          </Link>
        </DropdownMenuItem>
        {/* Signing out leaves you where you were reading — bouncing somebody
            to the front page threw away the page they were on. Unless the page
            needs an account, where staying put is an immediate bounce to the
            sign-in form, which is worse than either. */}
        <SignOutButton redirectUrl={afterSignOut(pathname)}>
          <DropdownMenuItem>
            <LogOut data-slot="icon" /> Sign out
          </DropdownMenuItem>
        </SignOutButton>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The footer for somebody with no session: the way back in, and only that.
 *
 * The offer to apply was here too, in blue, and every page a signed-out reader
 * can reach already makes it: the welcome page's hero, the cutoff at the end
 * of the directory and the library, the members-only card on Discussion. One
 * more copy pinned to the frame was the same offer on screen twice.
 *
 * It is not on a member page, a legacy page or the help centre, which carry no
 * cutoff to hang it from. Those want their own, phrased for what the reader is
 * looking at, rather than this one brought back.
 */
function SignedOutActions() {
  const { pathname, search } = useLocation();
  const back = encodeURIComponent(pathname + search);
  return (
    <div className="p-2 group-data-[collapsible=icon]:hidden">
      <Button variant="outline" className="w-full" asChild>
        <Link to={`/sign-in?${REDIRECT_PARAM}=${back}`}>Sign in</Link>
      </Button>
    </div>
  );
}

export function AppSidebar({
  member,
  isMember,
  hasFullAccess,
  queues,
  sessionPending = false,
}: {
  /** Null for somebody signed out: the sidebar is the same either way, and
   *  what is behind each link is the server's decision, not this one's. */
  member: MemberProfile | null;
  /** Applied and approved — what the membership button turns on, and not the
   *  same question as whether there is an account. */
  isMember?: boolean;
  /** Paid, gifted, or an admin: the viewer sees the whole directory rather
   *  than the redacted preview. Not the same as `isMember` — a free member
   *  has been approved and is still shown the preview. */
  hasFullAccess?: boolean;
  queues?: AdminQueues;
  /** Clerk has not finished loading, so who this is is not yet known. The
   *  footer waits rather than guessing — offering Sign in to somebody who is
   *  signed in, for the half-second before the answer arrives, is the flicker
   *  this replaced. */
  sessionPending?: boolean;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    // No collapse control on desktop, by design — the nav is short and always
    // wanted. `collapsible="icon"` all the same, so the ⌘B shortcut leaves an
    // icon rail rather than sliding the whole sidebar away with no button left
    // to bring it back.
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
                  <AfellMark />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="font-heading truncate">AFELL</span>
                  <span className="text-muted-foreground truncate text-xs">
                    Member Directory
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Members</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {memberNav.map((item) => {
                const external = item.url.startsWith("http");
                // Until `me` answers we do not know, and a padlock is not a
                // way of saying so. Measured at 1.4 seconds on a stubbed slow
                // session: Discussion sat locked, with the padlock, and then
                // became a link. Todd saw that and reasonably wondered whether
                // scope was being decided in the browser. It is not — every
                // one of these is gated server-side — but asserting the lock
                // before the answer arrives is the one place this nav claims
                // something it has not been told.
                const unknown = Boolean(item.membersOnly) && sessionPending;
                const shut = Boolean(item.membersOnly) && !sessionPending && !isMember;
                // Keyed on full access, not `isMember`: a free member has
                // applied and been approved and is still served the preview,
                // so badging on membership would tell them the directory is
                // complete when it is redacted.
                const previewing =
                  Boolean(item.preview) && !sessionPending && !hasFullAccess;
                const inside = (
                  <>
                    <item.icon />
                    <span>{item.title}</span>
                    {previewing && (
                      <span className="text-muted-foreground ml-auto text-[11px] tracking-normal">
                        Preview
                      </span>
                    )}
                  </>
                );
                return (
                  <SidebarMenuItem key={item.url}>
                    {unknown ? (
                      // Neutral: the name and its icon, nothing pressable and
                      // nothing claimed. It settles into the link or the lock.
                      <SidebarMenuButton
                        type="button"
                        disabled
                        className="text-muted-foreground/60"
                      >
                        {inside}
                      </SidebarMenuButton>
                    ) : shut ? (
                      // Not a link at all: following it lands on a Google
                      // Group that turns them away, which is a worse answer
                      // than this one. The tooltip says whose it is.
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {/* Not `aria-disabled`: SidebarMenuButton styles
                                that as `pointer-events-none`, which stops the
                                tooltip opening at all — a disabled control
                                that cannot say why it is disabled.
                                Clicking says the same thing as hovering,
                                because a tooltip is not an answer on a phone,
                                where there is no hover to have. */}
                            <SidebarMenuButton
                              type="button"
                              onClick={() => sayListservIsForMembers(navigate)}
                              className="text-muted-foreground cursor-pointer"
                            >
                              {inside}
                              <Lock className="ml-auto size-3.5" />
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            The AFELL listserv is for members
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <SidebarMenuButton
                        asChild
                        isActive={!external && pathname.startsWith(item.url)}
                      >
                        {external ? (
                          <a href={item.url} target="_blank" rel="noreferrer">
                            {inside}
                          </a>
                        ) : (
                          <Link to={item.url}>{inside}</Link>
                        )}
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {member && member.role !== "member" && (
          <SidebarGroup>
            <SidebarGroupLabel>
              {member.role === "admin" ? "Admin" : "Moderation"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {(member.role === "admin" ? adminNav : moderatorNav).map(
                  (item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          "exact" in item && item.exact
                            ? pathname === item.url
                            : pathname.startsWith(item.url) &&
                              (item.url !== "/admin" || pathname === "/admin")
                        }
                      >
                        <Link to={item.url}>
                          <item.icon />
                          <span>
                            {item.title}
                            {/* The queue in the label: a count you only see
                                after opening the page is a count nobody acts
                                on. */}
                            {"badge" in item &&
                              (queues?.[
                                item.badge as keyof AdminQueues
                              ] ?? 0) > 0 && (
                                <span className="text-muted-foreground">
                                  {" "}
                                  ({queues?.[item.badge as keyof AdminQueues]})
                                </span>
                              )}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        {/* Signed in, the offer to apply lives in the identity menu, on the
            same condition it used to be gated on here: a second blue button
            directly above the menu holding the same link was one offer made
            twice. Signed out there is no identity menu, so SignedOutActions
            still carries it. */}
        {sessionPending ? null : member ? (
          <IdentityMenu member={member} isMember={Boolean(isMember)} />
        ) : (
          <SignedOutActions />
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

export default AppSidebar;
